import React from 'react';
import { isNil, isUndefined } from 'lodash-es';

import { CONFIGURATOR_PREVIEW_LOAD_BUFFER } from '../constants';
import {
  ConfiguratorDesignArtOption,
  ConfiguratorDesignColorsOption,
  ConfiguratorDesignThemeOption,
  ConfiguratorSessionState,
  DesignVariantRequestParameters,
  SetSessionCallback,
} from '../types';
import { toURLSearchParams } from '../utils';

export function useIframe(
  designThemeOption: ConfiguratorDesignThemeOption | undefined,
  designColorsOption: ConfiguratorDesignColorsOption | undefined,
  designArtOption: ConfiguratorDesignArtOption | undefined,
  sessionState: ConfiguratorSessionState = {},
  setSession: SetSessionCallback,
) {
  const [iframeLoading, setIframeLoading] = React.useState<boolean>(false);
  const [debuggingTimestamp, setDebuggingTimestamp] = React.useState<number>(0);

  React.useEffect(() => {
    const observer = new MutationObserver((mutations, obs) => {
      const primaryIframeSelector = 'iframe#primary-all';
      const tertiaryIframeSelector = 'iframe#tertiary-all';

      const primary = document.querySelector<HTMLIFrameElement>(primaryIframeSelector);
      const tertiary = document.querySelector<HTMLIFrameElement>(tertiaryIframeSelector);

      if (!isNil(primary) && !isNil(tertiary)) {
        // Use DOM cloning to update the content immediately without any render delay, but is blocked by CORS on localhost
        // cloneIframeContent(event.target as HTMLIFrameElement, iframeElement);

        // Use browser cache to load the same URL without triggering another full load, but has render delay
        tertiary.src = primary.src;
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isUndefined(designThemeOption) && !isUndefined(designColorsOption) && !isUndefined(designArtOption)) {
      const parameters: DesignVariantRequestParameters = {
        wsid: designThemeOption.site_seed?.seed_data?.design_variant_id,
        //wsp: designThemeOption.site_seed?.seed_data?.page_id, // Currently not used so we can have full website build in preview
        wsp: '',
        wst: designColorsOption.skin_data.design_theme_id,
      };

      Object.entries(designArtOption.attributes ?? {}).forEach(([attrId, attrValue]) => {
        parameters[attrId] = attrValue;
      });

      const urlParams = toURLSearchParams(parameters);

      // Let's use the preview_url from seed data if available instead of default preview URL
      let baseUrl = !isUndefined(designThemeOption?.site_seed?.seed_data?.preview_url)
        ? designThemeOption.site_seed.seed_data.preview_url
        : window.config.previewDefaultUrl;

      if (baseUrl[baseUrl.length - 1] !== '/') {
        baseUrl += '/';
      }

      const nextPreviewUrl = `${baseUrl}?${urlParams.toString()}`;

      if (sessionState.previewUrl !== nextPreviewUrl) {
        console.log('Configurator[Preview]: Changed');

        setDebuggingTimestamp(Date.now());

        setIframeLoading(true);

        setSession({
          previewUrl: nextPreviewUrl,
        });
      }
    }
  }, [sessionState.previewUrl, setSession, designThemeOption, designColorsOption, designArtOption]);

  const cloneIframeContent = React.useCallback((source: HTMLIFrameElement, target: HTMLIFrameElement): void => {
    try {
      const sourceDocument = source.contentDocument;

      if (isNil(sourceDocument) || isNil(sourceDocument.documentElement)) {
        return;
      }

      const targetDocument = target.contentDocument;

      if (isNil(targetDocument)) {
        return;
      }

      // Clone the full document content
      const newDoctype = !isNil(sourceDocument.doctype)
        ? targetDocument.implementation.createDocumentType(
            sourceDocument.doctype.name,
            sourceDocument.doctype.publicId,
            sourceDocument.doctype.systemId,
          )
        : null;

      // Replace the entire document element with a deep clone
      const clonedHtml = sourceDocument.documentElement.cloneNode(true);

      // Clear existing content
      targetDocument.replaceChildren();

      if (!isNil(newDoctype)) {
        if (targetDocument.doctype) {
          targetDocument.replaceChild(newDoctype, targetDocument.doctype);
        } else {
          targetDocument.insertBefore(newDoctype, targetDocument.firstChild);
        }
      }

      targetDocument.appendChild(targetDocument.adoptNode(clonedHtml));
    } catch (err) {
      console.error('Configurator[Preview]: Failed to clone iframe content (possible cross-origin):', err);
    }
  }, []);

  const handleIframeLoad = React.useCallback<EventListener>(
    (event) => {
      const loadTime = Date.now() - debuggingTimestamp;
      // The event triggers when the index.html is loaded from server, but after that the site still has its loading icon.
      // For now, we could use an additional buffer of 2 seconds to ensure the site is fully loaded, or if the event time is longer,
      // double it (in case the connection is slow and affects downloading all the assets after loading icon).
      // TODO: maybe tweak the timing more
      const timeBuffer = loadTime > CONFIGURATOR_PREVIEW_LOAD_BUFFER ? loadTime : CONFIGURATOR_PREVIEW_LOAD_BUFFER;

      setTimeout(() => {
        console.log('Configurator[Preview]: Loaded', loadTime / 1000, 'seconds');

        setIframeLoading(false);
      }, timeBuffer);

      if (isNil(event.target)) {
        return;
      }

      const secondaryIframeSelectors = ['iframe#secondary-desktop', 'iframe#secondary-mobile'];

      secondaryIframeSelectors.forEach((iframeSelector) => {
        const iframeElement = document.querySelector<HTMLIFrameElement>(iframeSelector);

        if (isNil(iframeElement)) {
          return;
        }

        // To prevent scrolling via keyboard or focus
        iframeElement.setAttribute('tabindex', '-1');
        // Old attribute, still helps in some browsers
        iframeElement.setAttribute('scrolling', 'no');

        // Use DOM cloning to update the content immediately without any render delay, but is blocked by CORS on localhost
        // cloneIframeContent(event.target as HTMLIFrameElement, iframeElement);

        // Use browser cache to load the same URL without triggering another full load, but has render delay
        iframeElement.src = (event.target as HTMLIFrameElement).src;
      });
    },
    [debuggingTimestamp],
  );

  React.useEffect(() => {
    const primaryIframeSelector = 'iframe#primary-all';

    const iframeElement = document.querySelector<HTMLIFrameElement>(primaryIframeSelector);

    if (isNil(iframeElement)) {
      setIframeLoading(false);

      return;
    }

    iframeElement.addEventListener('load', handleIframeLoad);

    return () => {
      iframeElement.removeEventListener('load', handleIframeLoad);
    };
  }, [handleIframeLoad]);

  return React.useMemo(
    () => ({ iframeLoading, iframeUrl: sessionState.previewUrl }),
    [iframeLoading, sessionState.previewUrl],
  );
}
